<template>
  <div class="mb-40">
    <div class="flex items-center gap-3 mb-4">
      <Home class="w-7 h-7 text-foreground" />
      <h1 class="text-2xl font-semibold tracking-tight text-foreground">{{ getWelcomeString }}</h1>
    </div>

    <div v-if="!insightsAndTopline" class="space-y-4">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Skeleton v-for="i in 4" :key="`s${i}`" class="h-20" />
      </div>
      <Skeleton class="h-48" />
    </div>

    <div v-if="insightsAndTopline">
      <section class="mt-5 mb-4" v-if="insightsAndTopline != undefined && totalUsers != undefined">
        <div class="flex items-baseline justify-between mb-3">
          <h2 class="text-lg font-semibold text-foreground">Lifetime value</h2>
        </div>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card class="p-4">
            <div class="flex items-center justify-between mb-2">
              <p class="text-xs uppercase tracking-wide text-muted-foreground">Total enrolled users</p>
              <Users class="w-4 h-4 text-card-icon" />
            </div>
            <p class="text-2xl font-semibold text-foreground">{{ totalUsers }}</p>
          </Card>
          <Card class="p-4">
            <div class="flex items-center justify-between mb-2">
              <p class="text-xs uppercase tracking-wide text-muted-foreground">Transactions processed</p>
              <Activity class="w-4 h-4 text-card-icon" />
            </div>
            <p class="text-2xl font-semibold text-foreground">{{ insightsAndTopline.totalTransactionsProcessed.toLocaleString() }}</p>
          </Card>
          <Card class="p-4">
            <div class="flex items-center justify-between mb-2">
              <p class="text-xs uppercase tracking-wide text-muted-foreground">Total cashback</p>
              <BadgePoundSterling class="w-4 h-4 text-card-icon" />
            </div>
            <p class="text-2xl font-semibold text-foreground">£{{ insightsAndTopline.totalCashback.toFixed(2).toLocaleString() }}</p>
          </Card>
          <Card class="p-4">
            <div class="flex items-center justify-between mb-2">
              <p class="text-xs uppercase tracking-wide text-muted-foreground">Total commission</p>
              <BadgePoundSterling class="w-4 h-4 text-card-icon" />
            </div>
            <p class="text-2xl font-semibold text-foreground">£{{ insightsAndTopline.totalCommission.toFixed(2).toLocaleString() }}</p>
          </Card>
        </div>
        <p class="flex text-xs justify-end mt-2 text-muted-foreground">
          Last updated: {{ niceDateTimeLastUpdated(insightsAndTopline.createdOn) }}
        </p>
      </section>

      <div>
        <div>
          <Card class="h-full">
            <CardHeader class="flex-row items-start justify-between gap-4 space-y-0">
              <div class="flex-1">
                <CardTitle>Latest cashback events</CardTitle>
                <CardDescription>View your 7 latest cashback events</CardDescription>
              </div>
              <div class="flex items-center gap-2">
                <Button size="sm" id="createContract" @click="$router.push('/cashback-program/ledger')">
                  <BookOpenText class="w-4 h-4" />
                  View more
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <DataTable :headers="headersTable1" :items="cashbackObjects" :loading="loading" :error="cashbackError" @retry="getCashbackObjects(0, 7)" :hide-footer="true">
                <template #item-id="id">
                  <CopyId :value="id.id" :max-width="120" />
                </template>
                <template #item-createdOn="createdOn">
                  <span class="text-sm text-muted-foreground whitespace-nowrap">{{ niceDateTime2(createdOn.createdOn) }}</span>
                </template>
                <template #item-status="status">
                  <div class="flex items-center gap-2">
                    <Badge v-if="status.status === 'PAID'" variant="success">{{ status.status }}</Badge>
                    <Badge v-else-if="status.status === 'DECLINED'" variant="destructive">{{ status.status }}</Badge>
                    <Badge v-else variant="secondary">{{ status.status }}</Badge>
                    <button
                      v-if="showTestCashbackButton"
                      type="button"
                      class="text-xs font-medium text-primary underline-offset-2 hover:underline"
                      @click="() => { showManualCashbackUpdate = true; selectedCashback = status.id }"
                    >
                      Update status
                    </button>
                  </div>
                </template>
                <template #item-internalEndUserId="internalEndUserId">
                  <CopyId v-if="internalEndUserId.endUserId" :value="internalEndUserId.endUserId" :max-width="120" />
                  <span v-else class="text-muted-foreground">—</span>
                </template>
                <template #item-internalTransactionId="internalEndUserId">
                  <CopyId v-if="internalEndUserId.transactionId" :value="internalEndUserId.transactionId" :max-width="120" />
                  <span v-else class="text-muted-foreground">—</span>
                </template>
                <template #item-retailer="cashback">
                  <div v-if="cashback.programOffer && cashback.programOffer.retailer" class="flex items-center gap-2">
                    <img v-if="cashback.programOffer.retailer.logo" :src="cashback.programOffer.retailer.logo" class="w-6 h-6 rounded object-contain border bg-white" alt="" />
                    <span class="text-sm font-medium whitespace-nowrap">{{ cashback.programOffer.retailer.retailerName }}</span>
                  </div>
                  <div v-else class="flex items-center gap-2">
                    <span class="text-sm font-medium whitespace-nowrap">Cardlytics</span>
                  </div>
                </template>
                <template #item-transactionValue="cashback">
                  <span v-if="cashback.transactionAmount != null" class="text-sm tabular-nums whitespace-nowrap">{{ cashback.transactionAmount.currency }}{{ (Math.round(cashback.transactionAmount.amount * 100) / 100).toFixed(2) }}</span>
                  <span v-else class="text-sm text-muted-foreground whitespace-nowrap">Affiliate Transaction</span>
                </template>
                <template #item-userReceivable="cashback">
                  <span class="text-sm tabular-nums whitespace-nowrap">{{ cashback.payoutData.currency }}{{ (Math.round(cashback.payoutData.endUserCashbackReceivable * 100) / 100).toFixed(2) }}</span>
                </template>
                <template #item-publisherReceivable="cashback">
                  <span class="text-sm tabular-nums whitespace-nowrap">{{ cashback.payoutData.currency }}{{ (Math.round(cashback.payoutData.publisherCashbackReceivable * 100) / 100).toFixed(2) }}</span>
                </template>
                <template #item-declinedReason="cashback">
                  <Badge v-if="cashback.declinedReason" variant="destructive">{{ cashback.declinedReason }}</Badge>
                </template>
              </DataTable>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card class="mt-5">
        <CardHeader class="flex-row items-start justify-between gap-4 space-y-0">
          <div class="flex-1">
            <CardTitle>Offers ready to activate</CardTitle>
            <CardDescription>Activate your deactivated offers.</CardDescription>
          </div>
          <div class="flex items-center gap-2">
            <Button size="sm" id="createContract" @click="$router.push('/program-offers')">
              <BadgePercent class="w-4 h-4" />
              View program
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable :headers="offerTableHeaders" :items="retailers" :loading="loading"
            :error="error" @retry="getProgramOffers(serverOptions.page - 1, serverOptions.rowsPerPage)"
            v-model:server-options="serverOptions" :server-items-length="serverItemsLength">
            <template #item-retailer="retailers">
              <div class="flex justify-start align-top items-center gap-2 font-medium">
                <img class="w-12 h-12 rounded-md border border-border object-contain flex-shrink-0"
                  :src="retailers.offer.displayInformation.logo" />
                <div>
                  <p class="text-[12px] font-semibold mt-2">{{ retailers.retailer.retailerName }}</p>
                  <div class="mt-2 flex gap-1 items-center">
                    <Badge variant="outline">{{ formatOfferType(retailers.offer.offerType) }}</Badge>
                    <Badge variant="secondary">{{ retailers.offer.country }}</Badge>
                  </div>
                  <p class="text-[11px] text-muted-foreground mt-1 mb-2 italic">Program offer id: {{ retailers.id }}</p>
                </div>
              </div>
            </template>

            <template #item-audience="retailers">

              <div v-if="retailers.customerTierType == 'DEFAULT'">
                <p class="text-[12px] font-semibold">All users</p>
                <p>Available to everyone</p>
              </div>

              <div v-if="retailers.customerTierType == 'TIERED'">
                <p class="text-[12px] font-semibold">Targeted segment<span
                    v-if="selectedSegments(retailers).length > 1">s</span></p>
                <div class="mt-2" v-for="(tier, id) in selectedSegments(retailers)" :key="id">
                  <Badge variant="secondary">{{ tier }}</Badge>
                </div>
              </div>

              <div v-if="retailers.customerTierType == 'SET'">
                <p class="text-[12px] font-semibold">Targeted users</p>
                <p>Available to select users</p>
              </div>

            </template>


            <template #item-offertype="retailers">
              <Badge variant="outline">{{ formatOfferType(retailers.offer.offerType) }}</Badge>
            </template>
            <template #item-cashbackAmount="retailers">
              <Badge variant="outline">
                {{ retailers.cashbackEarnings[0].conditions[0].value.type === 'PERCENTAGE' ? '' : '£' }}{{ retailers.displayRules[0].highestCashbackPercentageAmount }}{{ retailers.cashbackEarnings[0].conditions[0].value.type === 'PERCENTAGE' ? '%' : '' }}
                <span v-if="retailers.cashbackEarnings[0].conditions[0].value.maximum" class="ml-1 font-normal">(max £{{ retailers.cashbackEarnings[0].conditions[0].value.maximum }})</span>
              </Badge>

              <div class="mt-1" v-if="retailers.offer.spendingCapped || retailers.offer.spendingCapAmount > 0">
                <p class="italic">Cashback capped at £{{ retailers.offer.spendingCapAmount }} per user</p>
              </div>
              <div class="mt-1" v-else>
                <p class="italic">No cap applied</p>
              </div>

            </template>

            <template #item-active="active">
              <Badge v-if="active.active" variant="default">Live</Badge>
              <Badge v-else variant="secondary">Deactivated</Badge>
            </template>
            <template #item-action="retailers">
              <div class="flex justify-end items-center gap-1">
                <Button size="sm" @click="$router.push(`/retailer/${retailers.retailer.id}/offer/${retailers.offer.id}`)">View</Button>
              </div>
            </template>

            <template #item-date="retailers">
              <div class="flex justify-end items-center gap-1">
                {{ niceDateTime(retailers.createdOn) }}
              </div>
            </template>
          </DataTable>
        </CardContent>
      </Card>

    </div>
  </div>
</template>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge,
  CopyId,
} from '@boogi/ui'
import {
  Home,
  Users,
  Activity,
  BadgePoundSterling,
  BookOpenText,
  BadgePercent,
} from 'lucide-vue-next'
import { DateTime } from 'luxon'
import { DataTable } from '@boogi/ui'


export default {
  name: 'LaunchPad',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    Button,
    Badge,
    CopyId,
    Home,
    Users,
    Activity,
    BadgePoundSterling,
    BookOpenText,
    BadgePercent,
  },
  data() {
    return {
      publisher: localStorage.getItem('publisher'),
      currentInvoiceData: undefined,
      name: localStorage.getItem('firstName'),
      insightsAndTopline: undefined,
      totalUsers: undefined,
      showTestCashbackButton: (localStorage.getItem('apiUrl') || '').includes('sandbox'),

      notifications: [],
      historicalNotifications: false,
      loadingNotifications: false,
      notificationCount: 0,
      page: 0,
      lastPage: false,

      cashbackStatuses: [
        { id: 'BEING_VERIFIED', text: 'BEING_VERIFIED' },
        { id: 'CREATED', text: 'CREATED' },
        { id: 'DECLINED', text: 'DECLINED' },
        { id: 'DISQUALIFIED', text: 'DISQUALIFIED' },
        { id: 'PAID', text: 'PAID' },
        { id: 'RECEIVED', text: 'RECEIVED' },
        { id: 'VERIFIED', text: 'VERIFIED' },
      ],

      selectStatus: [
        { id: 'BEING_VERIFIED', text: 'BEING_VERIFIED' },
        { id: 'CREATED', text: 'CREATED' },
        { id: 'DECLINED', text: 'DECLINED' },
        { id: 'PAID', text: 'PAID' },
        { id: 'RECEIVED', text: 'RECEIVED' },
        { id: 'REFUNDED', text: 'REFUNDED' },
        { id: 'VERIFIED', text: 'VERIFIED' },
      ],

      resetStatus: [
        { id: 'BEING_VERIFIED', text: 'BEING_VERIFIED' },
        { id: 'CREATED', text: 'CREATED' },
        { id: 'DECLINED', text: 'DECLINED' },
        { id: 'PAID', text: 'PAID' },
        { id: 'RECEIVED', text: 'RECEIVED' },
        { id: 'REFUNDED', text: 'REFUNDED' },
        { id: 'VERIFIED', text: 'VERIFIED' },
      ],

      statuses: [
        { id: 'BEING_VERIFIED', text: 'BEING_VERIFIED' },
        { id: 'CREATED', text: 'CREATED' },
        { id: 'DECLINED', text: 'DECLINED' },
        { id: 'DISQUALIFIED', text: 'DISQUALIFIED' },
        { id: 'PAID', text: 'PAID' },
        { id: 'RECEIVED', text: 'RECEIVED' },
        { id: 'REFUNDED', text: 'REFUNDED' },
        { id: 'VERIFIED', text: 'VERIFIED' },
      ],

      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength: undefined,

      loading: true,
      error: false,
      cashbackError: false,
      headersTable1: [
        { text: 'Id', value: 'id' },
        { text: 'Offer', value: 'retailer' },
        { text: 'Date created', value: 'createdOn' },
        { text: 'Status', value: 'status' },
        { text: 'Transaction id', value: 'internalTransactionId' },
        { text: 'End user receivable', value: 'userReceivable' },
      ],
      cashbackObjects: [],

      offerTableHeaders: [
        { text: 'Offer', value: 'retailer' },
        { text: 'Audience', value: 'audience' },
        { text: 'Cashback', value: 'cashbackAmount' },
        { text: 'Status', value: 'active' },
        { text: 'Action', value: 'action' },
      ],
      retailers: [],
      currentHour: new Date().getHours(),


      customerSegments: [],

      offerType: [],
      orderBy: 'MOSTPOPULAR',
    }
  },
  mounted() {
    document.title = 'Dashboard | Home'
  },
  computed: {

    getWelcomeString() {
      if (this.currentHour < 12) return `Good morning, ${this.name}! ☀️`
      if (this.currentHour < 18) return `Good afternoon, ${this.name}! 🌤️`
      return `Good evening, ${this.name}! 🌙`
    },
  },
  created() {
    this.getCurrentPayout()
    const now = DateTime.local()
    const startOfMonth = now.startOf('month')
    const endOfMonth = now.endOf('month')

    this.startDate = startOfMonth.minus({ month: 2 })
    this.endDate = endOfMonth
    this.formStart = this.startDate.toFormat('dd-MM-yyyy')
    this.formEnd = this.endDate.toFormat('dd-MM-yyyy')

    this.getToplineAndInsightData()
    this.getSummaryData()
    this.getCashbackObjects(0, 7)
    this.getNotifications(0)
    this.getProgramOffers(0, 25)
    this.getCustomerTiers()

  },

  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getProgramOffers(val.page - 1, val.rowsPerPage)
      },
    },
  },

  methods: {
    formatOfferType(t) {
      return ({ CARD_LINKED: 'Card-linked', AFFILIATE: 'Affiliate', IN_STORE: 'In-store', ONLINE: 'Online', PAYMENT: 'Payment-linked', CLICK_TO_ACTIVATE: 'Click to activate' })[t] || t
    },
    getCenterText() {
      return this.centerText // Retrieves text from data
    },

    async getCustomerTiers() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/customer-tier/for-offers`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.customerSegments = object.map((tier) => ({
            id: tier.id,
            text: tier.name,
          }))

          this.loading = false
        }
      } catch (error) {
        return
      }
    },

    selectedSegments(retailer) {
      if (retailer.customerTierType == 'TIERED') {
        const uniqueTexts = Array.from(
          new Set(
            retailer.selectedCustomerTiers
              .map((id) => {
                const match = this.customerSegments.find((seg) => seg.id === id)
                return match ? match.text : null
              })
              .filter(Boolean)
          )
        )

        return uniqueTexts
      } else {
        return []
      }
    },

    async getProgramOffers(page, size) {
      this.loading = true
      this.error = false
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer?page=${page}&size=${size}&status=false`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()

          this.serverItemsLength = object.totalElements
          this.retailers = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    niceDateTime2(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
    },

    toggleHistorical() {
      this.historicalNotifications = !this.historicalNotifications
      this.notifications = []
      this.page = 0

      if (this.historicalNotifications) {
        this.getHistoricalNotifications(0)
      } else {
        this.getNotifications(0)
      }
    },

    async getHistoricalNotifications(page) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/notifications/historic?page=${page}&size=5`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.notifications = [...this.notifications, ...object.content]
          this.lastPage = object.last
        }
      } catch (error) {
        console.error(error)
        return
      }
    },

    async getNotifications(page) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/notifications?page=${page}&size=5`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.notifications = [...this.notifications, ...object.content]
          this.lastPage = object.last
        }
      } catch (error) {
        console.error(error)
        return
      }
    },

    async getNotificationCount() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true
        const response = await this.$apiFetch(`${apiUrl}/private/api/client/notifications/count`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          var object = await response.json()
          this.notificationCount = object.count
        }
      } catch (error) {
        console.error(error)
        return
      }
    },

    async markAsSeen(notification) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        await this.$apiFetch(`${apiUrl}/private/api/client/notifications/${notification.id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })
      } catch (error) {
        console.error(error)
        return
      }
    },

    async getCashbackObjects(page, size) {
      this.loading = true
      this.cashbackError = false
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        if (this.selectStatus.length > 0) {
          var selectStatus = []
          this.selectStatus.forEach((element) => {
            selectStatus.push(element.id)
          })
        }

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/cashback-ledger?page=${page}&size=${size}&status=${selectStatus}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.cashbackObjects = object.content
        } else {
          this.cashbackError = true
        }
      } catch (e) {
        this.cashbackError = true
      } finally {
        this.loading = false
      }
    },

    getCenterTextOffers() {
      return this.centerTextOffers // Retrieves text from data
    },

    async getSummaryData() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(`${apiUrl}/private/api/client/audience/top-bar`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          var object = await response.json()
          this.totalUsers = object.totalUsers

        }
      } catch (error) {
        return
      }
    },

    async getToplineAndInsightData() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loadingCategories = true

        const response = await this.$apiFetch(`${apiUrl}/private/api/client/analytics/homepage`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          var object = await response.json()
          this.insightsAndTopline = object

        }
      } catch (error) {
        console.error(error)
        return
      }
    },

    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toFormat('MMMM yyyy')
    },

    niceDateTimeLastUpdated(dateObject) {
      return DateTime.fromMillis(dateObject).toFormat('dd LLLL yyyy HH:mm')
    },

    async getCurrentPayout() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(`${apiUrl}/private/api/client/payout/current`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          var object = await response.json()
          this.currentInvoiceData = object
        }
      } catch (error) {
        return
      }
    },
  },
}
</script>

<style></style>
